// components/process-units/Fractionation.js
import React, { useState } from 'react';

const Fractionation = ({ config, updateConfig }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Handle input changes
  const handleInputChange = (field, value) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      updateConfig({
        [field]: numValue
      });
    }
  };

  return (
    <div className="process-unit-card">
      <div 
        className="unit-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <h3>Fractionation</h3>
        <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
          {isExpanded ? '▼' : '▶'}
        </span>
      </div>
      
      {isExpanded && (
        <div className="unit-content">
          <p className="unit-description">
            Fractionation separates the NGL mixture into individual components (ethane, propane, butanes, 
            and natural gasoline) through a series of distillation columns. Each column operates at different
            pressure and temperature conditions specific to the component being separated.
          </p>
          
          <div className="parameter-group">
            <div className="parameter-row">
              <label>Configuration</label>
              <select 
                value={config.configuration || 'conventional'}
                onChange={(e) => updateConfig({ configuration: e.target.value })}
              >
                <option value="conventional">Conventional (De-ethanizer, De-propanizer, etc.)</option>
                <option value="sequentialOnly">Sequential Depropanizer+ (No De-ethanizer)</option>
                <option value="direct">Direct Split (Low/Mid/High Pressure Columns)</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Feed Temperature (°C)</label>
              <input
                type="number"
                min="-50"
                max="50"
                step="1"
                value={config.feedTemperature || -10}
                onChange={(e) => handleInputChange('feedTemperature', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>Feed Pressure (bar)</label>
              <input
                type="number"
                min="5"
                max="50"
                step="1"
                value={config.feedPressure || 25}
                onChange={(e) => handleInputChange('feedPressure', e.target.value)}
              />
            </div>
            
            <div className="parameter-row">
              <label>De-ethanizer (C2 Recovery)</label>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={config.deethanizer || false}
                  onChange={(e) => updateConfig({ deethanizer: e.target.checked })}
                />
              </div>
            </div>
            
            {config.deethanizer && (
              <>
                <div className="parameter-row">
                  <label>De-ethanizer Pressure (bar)</label>
                  <input
                    type="number"
                    min="10"
                    max="40"
                    step="1"
                    value={config.deetha_pressure || 28}
                    onChange={(e) => handleInputChange('deetha_pressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>De-ethanizer Reflux Ratio</label>
                  <input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={config.deetha_reflux || 5}
                    onChange={(e) => handleInputChange('deetha_reflux', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Ethane Product Purity (%)</label>
                  <input
                    type="number"
                    min="90"
                    max="99.9"
                    step="0.1"
                    value={config.ethane_purity || 99}
                    onChange={(e) => handleInputChange('ethane_purity', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>De-propanizer</label>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={config.depropanizer || true}
                  onChange={(e) => updateConfig({ depropanizer: e.target.checked })}
                />
              </div>
            </div>
            
            {config.depropanizer && (
              <>
                <div className="parameter-row">
                  <label>De-propanizer Pressure (bar)</label>
                  <input
                    type="number"
                    min="5"
                    max="25"
                    step="1"
                    value={config.depro_pressure || 18}
                    onChange={(e) => handleInputChange('depro_pressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>De-propanizer Reflux Ratio</label>
                  <input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={config.depro_reflux || 3}
                    onChange={(e) => handleInputChange('depro_reflux', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>Propane Product Purity (%)</label>
                  <input
                    type="number"
                    min="90"
                    max="99.9"
                    step="0.1"
                    value={config.propane_purity || 95}
                    onChange={(e) => handleInputChange('propane_purity', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>De-butanizer</label>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={config.debutanizer || true}
                  onChange={(e) => updateConfig({ debutanizer: e.target.checked })}
                />
              </div>
            </div>
            
            {config.debutanizer && (
              <>
                <div className="parameter-row">
                  <label>De-butanizer Pressure (bar)</label>
                  <input
                    type="number"
                    min="3"
                    max="15"
                    step="0.5"
                    value={config.debuta_pressure || 8}
                    onChange={(e) => handleInputChange('debuta_pressure', e.target.value)}
                  />
                </div>
                
                <div className="parameter-row">
                  <label>De-butanizer Reflux Ratio</label>
                  <input
                    type="number"
                    min="0.5"
                    max="10"
                    step="0.1"
                    value={config.debuta_reflux || 3}
                    onChange={(e) => handleInputChange('debuta_reflux', e.target.value)}
                  />
                </div>
              </>
            )}
            
            <div className="parameter-row">
              <label>Butane Splitter (iso/n-butane)</label>
              <div className="checkbox-container">
                <input
                  type="checkbox"
                  checked={config.butaneSplitter || false}
                  onChange={(e) => updateConfig({ butaneSplitter: e.target.checked })}
                />
              </div>
            </div>
            
            <div className="parameter-row">
              <label>Heat Source</label>
              <select 
                value={config.heatSource || 'steam'}
                onChange={(e) => updateConfig({ heatSource: e.target.value })}
              >
                <option value="steam">Steam</option>
                <option value="hotOil">Hot Oil</option>
                <option value="gasHeater">Natural Gas Fired</option>
              </select>
            </div>
            
            <div className="parameter-row">
              <label>Heat Integration Level</label>
              <select 
                value={config.heatIntegration || 'medium'}
                onChange={(e) => updateConfig({ heatIntegration: e.target.value })}
              >
                <option value="low">Low (Separate Utilities)</option>
                <option value="medium">Medium (Column Integration)</option>
                <option value="high">High (Advanced Heat Integration)</option>
              </select>
            </div>
          </div>
          
          <div className="unit-info-box">
            <h4>Engineering Notes</h4>
            <ul>
              <li>Fractionation is typically the most energy-intensive part of NGL processing.</li>
              <li>Reboilers account for most of the energy consumption in the fractionation section.</li>
              <li>Higher reflux ratios improve product purity but increase energy consumption.</li>
              <li>Heat integration between columns can significantly reduce overall energy requirements.</li>
              <li>Operating pressures affect both column diameter and reboiler temperature requirements.</li>
              <li>The number of theoretical stages in each column affects both capital cost and energy efficiency.</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fractionation;